import React, { useEffect, useState } from 'react';
import './App.css';
import LocalRoutes from './router/Routes';
import { hideBootSplashIfNoLoader } from './utils/bootSplash';
import { AlertTriangle } from './component/common/Icon';
import { GlobalNativeListener } from './component/GlobalNativeListener';
import GlobalVisitorApprovalOverlay from './component/GlobalVisitorApprovalOverlay';
import GlobalPushBanner from './component/GlobalPushBanner';
import GlobalSOSAlertOverlay from './component/GlobalSOSAlertOverlay';
import GlobalSubscriptionExpiredListener from './component/subscription/GlobalSubscriptionExpiredListener';
import { ToastProvider } from './component/common/ToastProvider';

class ErrorBoundary extends React.Component<
    { children: React.ReactNode },
    { hasError: boolean }
> {
    constructor(props: { children: React.ReactNode }) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() { return { hasError: true }; }
    componentDidCatch(error: Error, info: React.ErrorInfo) {
        console.error('App error boundary caught:', error, info);
    }
    render() {
        if (this.state.hasError) {
            return (
                <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    justifyContent: 'center', minHeight: '100svh', gap: 12, padding: 24,
                }}>
                    <AlertTriangle size={40} strokeWidth={1.75} />
                    <p style={{ fontSize: 16, fontWeight: 700, color: '#1a1a1a', margin: 0 }}>Something went wrong</p>
                    <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>Please reload the app</p>
                    <button
                        onClick={() => { this.setState({ hasError: false }); window.location.href = '/'; }}
                        style={{
                            padding: '10px 24px', borderRadius: 10, border: 'none',
                            background: '#1a1a1a', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                        }}
                    >Reload</button>
                </div>
            );
        }
        return this.props.children;
    }
}


const App: React.FC = () => {
    const [theme, setTheme] = useState<'dark' | 'light'>(() =>
        window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    );

    // Respond to system theme changes in real-time
    useEffect(() => {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e: MediaQueryListEvent) => setTheme(e.matches ? 'dark' : 'light');
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
    }, [theme]);

    // First paint with no <Loader> on screen (e.g. everything already cached) — the
    // boot splash has nothing to hand off to, so drop it now.
    useEffect(hideBootSplashIfNoLoader, []);

    // Pause animations when app is backgrounded to save energy
    useEffect(() => {
        const handleVisibility = () => {
            if (document.hidden) {
                document.documentElement.style.setProperty('--anim-play', 'paused');
                document.querySelectorAll('svg, canvas, video').forEach(el => {
                    (el as HTMLElement).style.visibility = 'hidden';
                });
            } else {
                document.documentElement.style.setProperty('--anim-play', 'running');
                document.querySelectorAll('svg, canvas, video').forEach(el => {
                    (el as HTMLElement).style.visibility = '';
                });
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, []);

    return (
        <ErrorBoundary>
            <ToastProvider>
                <GlobalNativeListener />
                <GlobalVisitorApprovalOverlay />
                <GlobalSOSAlertOverlay />
                <GlobalPushBanner />
                <GlobalSubscriptionExpiredListener />
                <LocalRoutes />
            </ToastProvider>
        </ErrorBoundary>
    );
}

export default App;